/**
 * Zutatenliste im Editor: Schnelleingabe und bearbeitbare Zeilen.
 */
import { useState } from 'react';
import type { RecipeDraft, Scaling } from '../api/types.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { parseIngredientLines } from '../lib/ingredientParser.ts';
import { useDragItems } from '../lib/useDragReorder.ts';
import { texts } from '../texts/de.ts';
import { DragHandle } from './DragHandle.tsx';

/** Eigenschaften der Zutatenliste. */
export interface IngredientListEditorProps {
  readonly ingredients: RecipeDraft['ingredients'];
  readonly onChange: (ingredients: RecipeDraft['ingredients']) => void;
  readonly onQuickAdd: (text: string) => void;
}

/** Auswahl der Umrechnungsart. */
const SCALING_OPTIONS: readonly { value: Scaling; label: string }[] = [
  { value: 'linear', label: texts.editor.scalingLinear },
  { value: 'rounded', label: texts.editor.scalingRounded },
  { value: 'sublinear', label: texts.editor.scalingSublinear },
  { value: 'fixed', label: texts.editor.scalingFixed },
];

/** Bearbeitbare Zutatenliste. */
export function IngredientListEditor({ ingredients, onChange, onQuickAdd }: IngredientListEditorProps) {
  const [quickText, setQuickText] = useState('');
  const [preview, setPreview] = useState<string[]>([]);
  const drag = useDragItems(ingredients, (ingredient, index) => ingredient.id ?? `neu-${index}`, onChange);

  /** Ändert ein Feld einer Zutat. */
  function update(index: number, changes: Partial<RecipeDraft['ingredients'][number]>) {
    onChange(ingredients.map((entry, position) => (position === index ? { ...entry, ...changes } : entry)));
  }

  /** Verschiebt eine Zutat. */
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    const a = ingredients[index];
    const b = ingredients[target];
    if (a === undefined || b === undefined) {
      return;
    }
    const next = [...ingredients];
    next[index] = b;
    next[target] = a;
    onChange(next);
  }

  return (
    <section className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <h2 className="mb-2 font-semibold">{texts.editor.ingredients}</h2>

      <details className="mb-3 rounded-xl bg-surface-sunken p-3">
        <summary className="cursor-pointer text-sm font-medium">{texts.editor.quickAdd}</summary>
        <p className="mt-2 text-xs text-content-muted">{texts.editor.quickAddHint}</p>
        <textarea
          value={quickText}
          onChange={(event) => {
            setQuickText(event.target.value);
            setPreview(
              parseIngredientLines(event.target.value).map((entry) =>
                `${entry.amount} ${entry.unit} ${entry.name}`.trim(),
              ),
            );
          }}
          rows={4}
          placeholder={'500 g Mehl Type 405\n1 Würfel Hefe\n1 Prise Salz'}
          className="mt-2 w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        />
        {preview.length === 0 ? null : (
          <p className="mt-1 text-xs text-content-muted">Erkannt: {preview.join(' · ')}</p>
        )}
        <button
          type="button"
          onClick={() => {
            onQuickAdd(quickText);
            setQuickText('');
            setPreview([]);
          }}
          disabled={quickText.trim() === ''}
          className="mt-2 rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.editor.quickAddApply}
        </button>
      </details>

      <ul className="space-y-2">
        {drag.entries.map(({ key, item: ingredient, index }) => (
          <li
            key={key}
            {...drag.itemProps(key)}
            className={`rounded-xl border border-border-subtle bg-surface-raised p-2 ${
              drag.dragging === key ? 'relative z-10 shadow-lg ring-2 ring-accent' : ''
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <DragHandle
                label={ingredient.name.trim() === '' ? texts.editor.name : ingredient.name}
                {...drag.handleProps(key)}
              />
              <input
                value={ingredient.amount}
                onChange={(event) => update(index, { amount: event.target.value })}
                placeholder={texts.editor.amount}
                aria-label={texts.editor.amount}
                className="w-16 rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-sm"
              />
              <input
                value={ingredient.unit}
                onChange={(event) => update(index, { unit: event.target.value })}
                placeholder={texts.editor.unit}
                aria-label={texts.editor.unit}
                className="w-20 rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-sm"
              />
              <input
                value={ingredient.name}
                onChange={(event) => update(index, { name: event.target.value })}
                placeholder={texts.editor.name}
                aria-label={texts.editor.name}
                className="min-w-40 flex-1 rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-sm"
              />
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  aria-label={texts.editor.moveUp}
                  className="rounded-lg border border-border-subtle px-2 py-1.5 text-content-muted"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  aria-label={texts.editor.moveDown}
                  className="rounded-lg border border-border-subtle px-2 py-1.5 text-content-muted"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => onChange(ingredients.filter((_, position) => position !== index))}
                  aria-label={texts.editor.remove}
                  className="rounded-lg border border-border-subtle px-2 py-1.5 text-content-muted"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                value={ingredient.note}
                onChange={(event) => update(index, { note: event.target.value })}
                placeholder={texts.editor.note}
                aria-label={texts.editor.note}
                className="min-w-32 flex-1 rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-xs"
              />
              <select
                value={ingredient.scaling}
                onChange={(event) => update(index, { scaling: event.target.value as Scaling })}
                aria-label={texts.editor.scaling}
                className="rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-xs"
              >
                {SCALING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() =>
          onChange([
            ...ingredients,
            { group: '', amount: '', unit: '', name: '', note: '', scaling: 'linear' },
          ])
        }
        className="mt-3 inline-flex items-center gap-2 rounded-xl border border-border-subtle px-3 py-2 text-sm"
      >
        <AppIcon name="plus" size={16} />
        {texts.editor.addIngredient}
      </button>
    </section>
  );
}
